import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import {
  BELGE_TURLERI,
  KANALLAR,
  KAYIT_TIPLERI,
  BOLUMLER,
  TAHSILAT_DURUMLARI,
  TAHSILAT_ETIKETLERI,
  TAHSILAT_BADGE,
  bugun,
} from "../lib/sabitler";
import { CiroForm } from "../components/CiroForm";
import { TahsilatDialog } from "../components/TahsilatDialog";
import { TahsilatRaporu } from "../components/TahsilatRaporu";
import { TakipRaporu } from "../components/TakipRaporu";
import { TahsilatAylikTablo } from "../components/TahsilatAylikTablo";
import { AyYilSecici, buAyTR, donemEtiketi } from "../components/AyYilSecici";
import { UstaRaporu } from "../components/UstaRaporu";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search,
  PlusCircle,
  ListFilter,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Trash2,
  Copy,
  FileUp,
  Banknote,
  Pencil,
  FileText,
  Sparkles,
  AlertTriangle,
  CalendarClock,
  Loader2,
  PieChart,
  User,
  Download,
  Wrench,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";

const PAGE_SIZE = 50;
const HEPSI = "__hepsi__";

const FiltreSelect = ({ label, value, onChange, options, testid, labels }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <Select value={value || HEPSI} onValueChange={(v) => onChange(v === HEPSI ? "" : v)}>
      <SelectTrigger className="h-10" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-card">
        <SelectItem value={HEPSI}>Tümü</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{labels?.[o] || o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const TahsilatBadge = ({ durum, vade }) => {
  // Vade sistemi: açık (Bekliyor) kayıtlar vade durumuna göre etiketlenir
  if (durum === "Bekliyor") {
    const bugunStr = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" });
    if (vade && vade < bugunStr) {
      return (
        <Badge className="bg-red-50 text-red-700 border border-red-200 hover:bg-red-50 whitespace-nowrap" title={`Vade: ${formatDate(vade)}`}>
          Vadesi Geçti
        </Badge>
      );
    }
    if (!vade) {
      return <span className="text-muted-foreground" data-testid="ciro-statu-bos">—</span>;
    }
    return (
      <Badge className="bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-50 whitespace-nowrap" title={`Vade: ${formatDate(vade)}`}>
        {`Vade: ${formatDate(vade)}`}
      </Badge>
    );
  }
  return <Badge className={`${TAHSILAT_BADGE[durum] || ""} whitespace-nowrap`}>{TAHSILAT_ETIKETLERI[durum] || durum}</Badge>;
};

// Fatura dosyasını (PDF/görsel) yetkili istekle indirip yeni sekmede açar
const pdfAc = async (pdfId) => {
  try {
    const res = await api.get(`/ciro/fatura-pdf/${pdfId}`, { responseType: "blob" });
    const tip = res.headers["content-type"] || "application/pdf";
    const url = URL.createObjectURL(new Blob([res.data], { type: tip }));
    window.open(url, "_blank", "noopener");
  } catch {
    toast.error("Dosya açılamadı.");
  }
};

const FATURA_UZANTILAR = [".pdf", ".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];

export default function CiroPage() {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["yeni", "liste", "takip", "ozet", "usta"].includes(t) ? t : "yeni";
  });

  // form state
  const [formKey, setFormKey] = useState(0);
  const [prefill, setPrefill] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  // list state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    baslangic: "",
    bitis: "",
    kayit_tipi: "",
    belge_turu: "",
    kanal: "",
    bolum: "",
    tahsilat_durumu: "",
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  // selection & dialogs
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDate, setBulkDate] = useState(bugun());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // tahsilat dialogu
  const [tahsilatCiroId, setTahsilatCiroId] = useState(null);

  // AI PDF okuma
  const fileInputRef = useRef(null);
  const [aiOkuyor, setAiOkuyor] = useState(false);
  const [aiSonuc, setAiSonuc] = useState(null); // {dosya_adi, hata}
  const [mukerrer, setMukerrer] = useState(null); // {mevcut, alanlar, pdf_id, dosya_adi, hata}
  const [mukerrerIsleniyor, setMukerrerIsleniyor] = useState(false);
  const [silTahsilat, setSilTahsilat] = useState(null);
  const [silTahsilatIsleniyor, setSilTahsilatIsleniyor] = useState(false);
  const [takipSirala, setTakipSirala] = useState("vade"); // vade | is_emri_asc | is_emri_desc

  // takip listesi
  const [takip, setTakip] = useState(null);
  const [takipLoading, setTakipLoading] = useState(false);
  const [takipDonem, setTakipDonem] = useState(buAyTR); // {yil, ay|null}
  const [takipTetik, setTakipTetik] = useState(0);

  // tahsilat özeti
  const [ozet, setOzet] = useState(null);
  const [ozetLoading, setOzetLoading] = useState(false);
  const [ozetDonem, setOzetDonem] = useState(buAyTR);

  const fetchOzet = useCallback(async () => {
    setOzetLoading(true);
    try {
      const res = await api.get("/ciro/tahsilat-ozeti", { params: { yil: ozetDonem.yil, ay: ozetDonem.ay || undefined } });
      setOzet(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Tahsilat özeti yüklenemedi."));
    } finally {
      setOzetLoading(false);
    }
  }, [ozetDonem]);

  useEffect(() => {
    if (tab === "ozet") fetchOzet();
  }, [tab, fetchOzet]);

  const tedarikciExcelIndir = async () => {
    try {
      const res = await api.get("/ciro/tedarikci-odemeleri/excel", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tedarikci_odemeleri_${bugun()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Tedarikçi ödemeleri raporu indirildi.");
    } catch (err) {
      toast.error(getErrMsg(err, "Excel raporu indirilemedi."));
    }
  };

  const fetchTakip = useCallback(async () => {
    setTakipLoading(true);
    try {
      const res = await api.get("/ciro/takip", { params: { yil: takipDonem.yil, ay: takipDonem.ay || undefined } });
      setTakip(res.data);
      setTakipTetik((t) => t + 1);
    } catch (err) {
      toast.error(getErrMsg(err, "Takip listesi yüklenemedi."));
    } finally {
      setTakipLoading(false);
    }
  }, [takipDonem]);

  useEffect(() => {
    fetchTakip();
  }, [fetchTakip]);

  const takipKayitlar = useMemo(() => {
    if (!takip) return [];
    if (takipSirala === "vade") return takip.kayitlar;
    const arr = [...takip.kayitlar].sort((a, b) =>
      (a.is_emri_no || "").localeCompare(b.is_emri_no || "", "tr", { numeric: true })
    );
    return takipSirala === "is_emri_desc" ? arr.reverse() : arr;
  }, [takip, takipSirala]);

  const prefillDoldur = (alanlar, pdf_id, dosya_adi, hata) => {
    if (alanlar) {
        // Fatura tipine göre kanal önerisi (AI önerisi — kullanıcı değiştirebilir)
        const kanalOneri =
          alanlar.fatura_tipi === "bireysel" ? "Bireysel"
          : alanlar.fatura_tipi === "kurumsal" ? "Kurumsal"
          : "";
        // Belge tipine göre belge türü önerisi
        const belgeTuruOneri =
          alanlar.belge_tipi === "e_fatura" ? "E-Fatura"
          : alanlar.belge_tipi === "e_arsiv" ? "E-Arşiv"
          : alanlar.belge_tipi === "fatura" ? "Fatura"
          : "";
        // AI gelir türü tespiti: araç / hizmet / diğer
        const gelirTuru =
          alanlar.gelir_turu === "hizmet" ? "Hizmet"
          : alanlar.gelir_turu === "diger" ? "Diğer"
          : "Araç";
        setPrefill({
          tarih: alanlar.fatura_tarihi || bugun(),
          gelir_turu: gelirTuru,
          kayit_tipi: alanlar.belge_tipi === "is_emri" ? "İş Emri" : "Fatura",
          belge_turu: belgeTuruOneri,
          belge_no: alanlar.fatura_no || "",
          // TAM AYRIM: İş Emri No yalnızca is_emri_no'dan, Dosya No yalnızca dosya_no'dan dolar.
          // Belgede ikisi de varsa ikisi de kendi alanına yazılır; biri yoksa alanı boş kalır.
          is_emri_no: gelirTuru === "Hizmet" ? "" : (alanlar.is_emri_no || ""),
          musteri_aciklama: alanlar.musteri_aciklama || alanlar.sigorta_sirketi || "",
          sigorta_sirketi: gelirTuru !== "Hizmet" && alanlar.fatura_tipi === "sigorta" ? (alanlar.sigorta_sirketi || "") : "",
          plaka: gelirTuru === "Hizmet" ? "" : (alanlar.plaka || ""),
          // Dosya No: dosya / hasar dosya numarası — iş emri numarasıyla asla karışmaz
          plaka_dosya_no: gelirTuru === "Hizmet" ? "" : (alanlar.dosya_no || alanlar.hasar_dosya_no || ""),
          // Hizmet faturası alanları — AI otomatik doldurur
          hizmet_turu: gelirTuru === "Hizmet" ? (alanlar.hizmet_turu || "") : "",
          hizmet_aciklama: gelirTuru === "Hizmet" ? (alanlar.hizmet_aciklama || "") : "",
          proje_sozlesme_no: gelirTuru === "Hizmet" ? (alanlar.proje_sozlesme_no || "") : "",
          matrah: alanlar.matrah != null ? alanlar.matrah : "",
          kdv: alanlar.kdv != null ? alanlar.kdv : "",
          kanal: kanalOneri,
          bolum: gelirTuru === "Hizmet" ? "" : (alanlar.bolum || (alanlar.tahmini_bolumler?.length === 1 ? alanlar.tahmini_bolumler[0] : "")),
          // AI bölüm önerisi: birden fazla bölüm algılandıysa kırılım otomatik açılır (kullanıcı onaylar/düzeltir)
          tahmini_bolumler: gelirTuru === "Hizmet" ? [] : (alanlar.tahmini_bolumler || []),
          fatura_pdf_id: pdf_id,
        });
        setFormKey((k) => k + 1);
        const belgeMetin =
          alanlar.belge_tipi === "is_emri" ? "İş emri"
          : alanlar.belge_tipi === "proforma" ? "Proforma"
          : alanlar.belge_tipi === "servis_formu" ? "Servis formu"
          : alanlar.belge_tipi === "e_fatura" ? "e-Fatura"
          : alanlar.belge_tipi === "e_arsiv" ? "e-Arşiv fatura"
          : "Fatura";
        const tipMetin =
          gelirTuru === "Hizmet" ? `${belgeMetin} (HİZMET faturası — ${alanlar.hizmet_turu || "tür seçin"})`
          : alanlar.fatura_tipi === "sigorta" ? `${belgeMetin} (sigorta işi)`
          : alanlar.fatura_tipi === "kurumsal" ? `${belgeMetin} (ücretli — kurumsal)`
          : alanlar.fatura_tipi === "bireysel" ? `${belgeMetin} (ücretli — bireysel)`
          : belgeMetin;
        toast.success(`${tipMetin} AI ile okundu — alanları kontrol edip bölümü seçin.`);
      } else {
        setPrefill({ fatura_pdf_id: pdf_id });
        setFormKey((k) => k + 1);
        toast.warning(hata || "Alanlar okunamadı, elle doldurun. Dosya yine de kayda eklenecek.");
      }
    setAiSonuc({ dosya_adi, hata, tip: alanlar?.fatura_tipi });
  };

  const handlePdfSec = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const uzantiOk = FATURA_UZANTILAR.some((u) => file.name.toLowerCase().endsWith(u));
    if (!uzantiOk) return toast.error("Desteklenen formatlar: PDF, JPG, PNG, WEBP, HEIC.");
    setAiOkuyor(true);
    setAiSonuc(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/ciro/fatura-oku", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const { pdf_id, dosya_adi, alanlar, hata } = res.data;
      if (res.data.mukerrer && alanlar) {
        setMukerrer({ mevcut: res.data.mukerrer, alanlar, pdf_id, dosya_adi, hata });
        return;
      }
      prefillDoldur(alanlar, pdf_id, dosya_adi, hata);
    } catch (err) {
      toast.error(getErrMsg(err, "Fatura okunamadı."));
    } finally {
      setAiOkuyor(false);
    }
  };

  const handleMukerrerEkle = async () => {
    const { mevcut, alanlar, pdf_id } = mukerrer;
    setMukerrerIsleniyor(true);
    try {
      const belgeTuru =
        alanlar.belge_tipi === "e_fatura" ? "E-Fatura"
        : alanlar.belge_tipi === "e_arsiv" ? "E-Arşiv"
        : alanlar.belge_tipi === "fatura" ? "Fatura"
        : "";
      const res = await api.post(`/ciro/${mevcut.id}/fatura-isle`, {
        belge_no: alanlar.fatura_no || "",
        belge_turu: belgeTuru,
        belge_tipi: alanlar.belge_tipi || "",
        matrah: alanlar.matrah,
        kdv: alanlar.kdv,
        fatura_pdf_id: pdf_id,
        is_emri_no: alanlar.is_emri_no || "",
        dosya_no: alanlar.dosya_no || alanlar.hasar_dosya_no || "",
        plaka: alanlar.plaka || "",
        sigorta_sirketi: alanlar.sigorta_sirketi || "",
      });
      toast.success(res.data.message || "Belge mevcut kayda işlendi.");
      setMukerrer(null);
      fetchList();
      fetchTakip();
    } catch (err) {
      toast.error(getErrMsg(err, "Belge mevcut kayda işlenemedi."));
    } finally {
      setMukerrerIsleniyor(false);
    }
  };

  // Mükerrer eşleşmede yüklenen belge gerçek fatura mı? (iş emri/proforma ise mevcut kayıt korunur)
  const mukerrerFaturaMi = !mukerrer?.alanlar?.belge_tipi ||
    ["e_fatura", "e_arsiv", "fatura"].includes(mukerrer.alanlar.belge_tipi);

  // Yüklenen belgenin, mevcut kayıtta EKSİK olan bilgileri (tamamlanacaklar listesi)
  const mukerrerEksikler = (() => {
    if (!mukerrer?.mevcut || !mukerrer?.alanlar) return [];
    const m = mukerrer.mevcut;
    const a = mukerrer.alanlar;
    const list = [];
    if (a.is_emri_no && !(m.is_emri_no || "").trim()) list.push(["İş Emri No", a.is_emri_no]);
    const yeniDosyaNo = a.dosya_no || a.hasar_dosya_no;
    if (yeniDosyaNo && !((m.plaka_dosya_no || m.hasar_dosya_no || "").trim())) list.push(["Dosya No", yeniDosyaNo]);
    if (a.fatura_no && !(m.belge_no || "").trim()) list.push(["Fatura No", a.fatura_no]);
    if (a.plaka && !(m.plaka || "").trim()) list.push(["Plaka", a.plaka]);
    if (a.sigorta_sirketi && !(m.sigorta_sirketi || "").trim()) list.push(["Sigorta", a.sigorta_sirketi]);
    return list;
  })();

  const handleTahsilatSil = async () => {
    setSilTahsilatIsleniyor(true);
    try {
      await api.delete(`/ciro/tahsilat/${silTahsilat.id}`);
      toast.success("Tahsilat kaydı silindi (loglandı).");
      setSilTahsilat(null);
      fetchOzet();
      fetchList();
      fetchTakip();
    } catch (err) {
      toast.error(getErrMsg(err, "Tahsilat silinemedi."));
    } finally {
      setSilTahsilatIsleniyor(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
      if (debouncedSearch) params.q = debouncedSearch;
      const res = await api.get("/ciro", { params });
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıtlar yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [page, filters, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
    setSelected(new Set());
  };

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const res = await api.post("/ciro", payload);
      toast.success("Ciro kaydı eklendi.");
      setLastSaved(res.data);
      setPrefill(null);
      setAiSonuc(null);
      setFormKey((k) => k + 1);
      fetchList();
      fetchTakip();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt eklenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const handleSameInvoice = () => {
    if (!lastSaved) return;
    setPrefill({
      tarih: lastSaved.tarih,
      musteri_aciklama: lastSaved.musteri_aciklama,
      belge_no: lastSaved.belge_no,
    });
    setLastSaved(null);
    setFormKey((k) => k + 1);
  };

  const handleUpdate = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/ciro/${editTarget.id}`, payload);
      toast.success("Kayıt güncellendi.");
      setEditTarget(null);
      fetchList();
      fetchTakip();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt güncellenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/ciro/${deleteTarget.id}`);
      toast.success("Kayıt silindi.");
      setDeleteTarget(null);
      setEditTarget(null);
      fetchList();
      fetchTakip();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt silinemedi."));
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const kayitlar = data?.kayitlar || [];
  const allOnPageSelected = kayitlar.length > 0 && kayitlar.every((k) => selected.has(k.id));

  const toggleSelectAll = () => {
    setSelected((s) => {
      const next = new Set(s);
      if (allOnPageSelected) kayitlar.forEach((k) => next.delete(k.id));
      else kayitlar.forEach((k) => next.add(k.id));
      return next;
    });
  };

  const handleBulkTahsilat = async () => {
    if (!bulkDate) return toast.error("Tahsilat tarihi seçin.");
    setBulkSaving(true);
    try {
      const res = await api.post("/ciro/toplu-tahsilat", {
        ids: Array.from(selected),
        tahsilat_tarihi: bulkDate,
      });
      toast.success(res.data.message);
      setSelected(new Set());
      setBulkOpen(false);
      fetchList();
      fetchTakip();
    } catch (err) {
      toast.error(getErrMsg(err, "Toplu güncelleme yapılamadı."));
    } finally {
      setBulkSaving(false);
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data?.toplam_kayit || 0) / PAGE_SIZE)),
    [data]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-12 max-w-full justify-start overflow-x-auto bg-secondary">
          <TabsTrigger value="yeni" className="h-10 gap-1.5 px-4" data-testid="ciro-tab-yeni">
            <PlusCircle className="h-4 w-4" />
            Yeni Kayıt
          </TabsTrigger>
          <TabsTrigger value="liste" className="h-10 gap-1.5 px-4" data-testid="ciro-tab-liste">
            <ListFilter className="h-4 w-4" />
            Kayıtlar {data ? `(${data.toplam_kayit.toLocaleString("tr-TR")})` : ""}
          </TabsTrigger>
          <TabsTrigger value="takip" className="h-10 gap-1.5 px-4" data-testid="ciro-tab-takip">
            <CalendarClock className="h-4 w-4" />
            Takip Edilecek Dosyalar {takip ? `(${takip.ozet.adet.toLocaleString("tr-TR")})` : ""}
          </TabsTrigger>
          <TabsTrigger value="ozet" className="h-10 gap-1.5 px-4" data-testid="ciro-tab-ozet">
            <PieChart className="h-4 w-4" />
            Tahsilat Özeti
          </TabsTrigger>
          <TabsTrigger value="usta" className="h-10 gap-1.5 px-4" data-testid="ciro-tab-usta">
            <Wrench className="h-4 w-4" />
            Usta Performansı
          </TabsTrigger>
        </TabsList>

        {/* ---- YENİ KAYIT ---- */}
        <TabsContent value="yeni" className="mt-4">
          <div className="rounded-xl border bg-card p-4 sm:p-6 shadow-[0_1px_0_rgba(14,27,46,0.06),0_10px_30px_rgba(14,27,46,0.06)] w-full">
            {/* AI PDF Fatura Okuma */}
            <div className="mb-5 rounded-lg border border-dashed border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/[0.03] p-4" data-testid="ciro-pdf-yukle-alani">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Sparkles className="h-5 w-5 shrink-0 text-[hsl(var(--primary))]" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">Fatura / Proforma / İş Emri Yükle (PDF veya Fotoğraf) — AI ile Otomatik Doldur</p>
                    <p className="text-xs text-muted-foreground">
                      AI belgeyi otomatik sınıflandırır: <strong>araç faturası mı, hizmet faturası mı</strong> algılar ve ilgili alanları (hizmet türü, proje/sözleşme no, dosya no, iş emri no, tutarlar) kendisi doldurur.
                    </p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp,.heic,.heif"
                  onChange={handlePdfSec}
                  className="hidden"
                  data-testid="ciro-pdf-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={aiOkuyor}
                  className="min-h-[44px] gap-1.5"
                  data-testid="ciro-pdf-yukle-button"
                >
                  {aiOkuyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                  {aiOkuyor ? "AI okuyor…" : "PDF / Görsel Yükle"}
                </Button>
              </div>
              {aiSonuc && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2 text-xs text-blue-800" data-testid="ciro-pdf-ai-banner">
                  {aiSonuc.hata ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                  <span>
                    <strong>{aiSonuc.dosya_adi}</strong> yüklendi ve kayda eklenecek.
                    {aiSonuc.tip && (
                      <> AI tespiti: <strong>{aiSonuc.tip === "sigorta" ? "Sigorta faturası" : aiSonuc.tip === "kurumsal" ? "Kurumsal fatura" : "Bireysel fatura"}</strong>.</>
                    )}{" "}
                    {aiSonuc.hata
                      ? aiSonuc.hata
                      : "AI önerisi kesin kabul edilmez — alanları kontrol edin, yanlış okunanı düzeltin ve bölümü seçin."}
                  </span>
                </div>
              )}
            </div>
            <h2 className="font-display text-lg font-semibold mb-4">Ciro Girişi</h2>
            <CiroForm
              key={formKey}
              initial={prefill}
              onSubmit={handleCreate}
              submitting={saving}
            />
            {lastSaved && (
              <div
                className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3"
                data-testid="ciro-saved-banner"
              >
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <p className="text-sm text-emerald-800">
                  Kayıt eklendi: <strong>{formatTL(lastSaved.toplam, 2)}</strong> — {lastSaved.bolum}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSameInvoice}
                  className="ml-auto gap-1.5 min-h-[40px] border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                  data-testid="ciro-same-invoice-button"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Aynı faturaya yeni bölüm satırı ekle
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ---- LİSTE ---- */}
        <TabsContent value="liste" className="mt-4 space-y-4">
          {/* Filtreler */}
          <div className="rounded-xl border bg-card p-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Başlangıç</Label>
                <Input
                  type="date"
                  value={filters.baslangic}
                  onChange={(e) => setFilter("baslangic", e.target.value)}
                  className="h-10"
                  data-testid="ciro-filter-baslangic"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Bitiş</Label>
                <Input
                  type="date"
                  value={filters.bitis}
                  onChange={(e) => setFilter("bitis", e.target.value)}
                  className="h-10"
                  data-testid="ciro-filter-bitis"
                />
              </div>
              <FiltreSelect label="Tip" value={filters.kayit_tipi} onChange={(v) => setFilter("kayit_tipi", v)} options={KAYIT_TIPLERI} testid="ciro-filter-kayit-tipi" />
              <FiltreSelect label="Belge Türü" value={filters.belge_turu} onChange={(v) => setFilter("belge_turu", v)} options={BELGE_TURLERI} testid="ciro-filter-belge-turu" />
              <FiltreSelect label="Kanal" value={filters.kanal} onChange={(v) => setFilter("kanal", v)} options={KANALLAR} testid="ciro-filter-kanal" />
              <FiltreSelect label="Bölüm" value={filters.bolum} onChange={(v) => setFilter("bolum", v)} options={BOLUMLER} testid="ciro-filter-bolum" />
              <FiltreSelect label="Statü" value={filters.tahsilat_durumu} onChange={(v) => setFilter("tahsilat_durumu", v)} options={TAHSILAT_DURUMLARI} labels={TAHSILAT_ETIKETLERI} testid="ciro-filter-tahsilat" />
              <div className="space-y-1 col-span-2 sm:col-span-1">
                <Label className="text-xs text-muted-foreground">Ara</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Müşteri, belge, iş emri, plaka…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-10 pl-8"
                    data-testid="ciro-filter-search"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Toplu seçim aksiyonu */}
          {selected.size > 0 && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-lg bg-[hsl(var(--primary))] px-4 py-3 text-white"
              data-testid="ciro-bulk-bar"
            >
              <span className="text-sm">
                <strong>{selected.size}</strong> kayıt seçildi
              </span>
              <Button
                size="sm"
                onClick={() => { setBulkDate(bugun()); setBulkOpen(true); }}
                className="ml-auto min-h-[40px] gap-1.5 bg-[hsl(var(--accent))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--accent))]/90 font-medium"
                data-testid="ciro-bulk-tahsilat-button"
              >
                <CheckCircle2 className="h-4 w-4" />
                Tahsil Edildi olarak işaretle
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelected(new Set())}
                className="min-h-[40px] text-white/70 hover:text-white hover:bg-white/10"
                data-testid="ciro-bulk-clear-button"
              >
                Vazgeç
              </Button>
            </div>
          )}

          {/* Liste */}
          {loading && !data ? (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop tablo */}
              <div className="hidden md:block rounded-xl border bg-card overflow-hidden">
                <div className="w-full overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ciro-table">
                    <thead className="bg-muted/50">
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-3 w-10">
                          <Checkbox
                            checked={allOnPageSelected}
                            onCheckedChange={toggleSelectAll}
                            data-testid="ciro-select-all-checkbox"
                            aria-label="Tümünü seç"
                          />
                        </th>
                        <th className="px-3 py-3">Tarih</th>
                        <th className="px-3 py-3">Tip</th>
                        <th className="px-3 py-3">Müşteri</th>
                        <th className="px-3 py-3">Sigorta Şirketi</th>
                        <th className="px-3 py-3">Plaka</th>
                        <th className="px-3 py-3">Dosya No</th>
                        <th className="px-3 py-3">İş Emri No</th>
                        <th className="px-3 py-3">Bölüm</th>
                        <th className="px-3 py-3 text-right">Matrah</th>
                        <th className="px-3 py-3 text-right">KDV</th>
                        <th className="px-3 py-3 text-right">Toplam</th>
                        <th className="px-3 py-3">Statü</th>
                        <th className="px-3 py-3 text-right">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kayitlar.length === 0 ? (
                        <tr>
                          <td colSpan={14} className="px-3 py-10 text-center text-muted-foreground" data-testid="ciro-empty-message">
                            Filtrelere uyan kayıt bulunamadı.
                          </td>
                        </tr>
                      ) : (
                        kayitlar.map((k) => (
                          <tr
                            key={k.id}
                            className="border-t border-border/60 hover:bg-[hsl(var(--primary))]/[0.03] cursor-pointer transition-colors"
                            onClick={() => setEditTarget(k)}
                            data-testid="ciro-table-row"
                          >
                            <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selected.has(k.id)}
                                onCheckedChange={() => toggleSelect(k.id)}
                                data-testid="ciro-row-checkbox"
                                aria-label="Seç"
                              />
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{formatDate(k.tarih)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <Badge
                                className={`border text-[10px] font-semibold ${(k.kayit_tipi || "Fatura") === "İş Emri" ? "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-50" : "border-border bg-muted/60 text-foreground/70 hover:bg-muted/60"}`}
                                data-testid="ciro-row-tip-badge"
                              >
                                {k.kayit_tipi || "Fatura"}
                              </Badge>
                            </td>
                            <td className="px-3 py-2.5 max-w-[200px] truncate" title={k.musteri_aciklama}>
                              {k.musteri_aciklama || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 max-w-[160px] truncate" title={k.sigorta_sirketi}>
                              {k.sigorta_sirketi || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums" data-testid="ciro-row-plaka">
                              {k.plaka || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td
                              className="px-3 py-2.5 max-w-[130px] truncate whitespace-nowrap tabular-nums"
                              title={k.hasar_dosya_no || k.plaka_dosya_no}
                              data-testid="ciro-row-dosya-no"
                            >
                              {(k.hasar_dosya_no || k.plaka_dosya_no) || (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td
                              className="px-3 py-2.5 max-w-[130px] truncate whitespace-nowrap tabular-nums"
                              title={k.is_emri_no}
                              data-testid="ciro-row-is-emri-no"
                            >
                              {k.is_emri_no || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              {k.bolum_dagilimi?.length > 1 ? (
                                <span title={k.bolum_dagilimi.map((p) => `${p.bolum}: ${formatTL(p.tutar)}`).join(" • ")} data-testid="ciro-row-bolum-dagilim">
                                  {k.bolum} <span className="text-[10px] font-semibold text-sky-700">+{k.bolum_dagilimi.length - 1}</span>
                                </span>
                              ) : (
                                k.bolum || <span className="text-muted-foreground italic text-xs">Belirtilmemiş</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.matrah, 2)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.kdv, 2)}</td>
                            <td className="px-3 py-2.5 text-right font-medium tabular-nums whitespace-nowrap">{formatTL(k.toplam, 2)}</td>
                            <td className="px-3 py-2.5">
                              <TahsilatBadge durum={k.tahsilat_durumu} vade={k.vade_tarihi} />
                              {k.tahsilat_durumu === "Kısmi" && k.kalan_bakiye != null && (
                                <p className="mt-0.5 text-[10px] text-amber-700 tabular-nums whitespace-nowrap">Kalan: {formatTL(k.kalan_bakiye, 2)}</p>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setEditTarget(k)}
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                title="Kaydı düzenle"
                                data-testid="ciro-row-edit-button"
                                aria-label="Kaydı düzenle"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              {k.tahsilat_durumu !== "Tahsil Edildi" && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setTahsilatCiroId(k.id)}
                                  className="h-8 w-8 text-emerald-700 hover:text-emerald-800"
                                  title="Tahsilat ekle"
                                  data-testid="ciro-row-tahsilat-button"
                                  aria-label="Tahsilat ekle"
                                >
                                  <Banknote className="h-4 w-4" />
                                </Button>
                              )}
                              {k.fatura_pdf_id && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => pdfAc(k.fatura_pdf_id)}
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  title="Fatura PDF'ini aç"
                                  data-testid="ciro-row-pdf-button"
                                  aria-label="Fatura PDF"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteTarget(k)}
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                title="Kaydı sil"
                                data-testid="ciro-row-delete-button"
                                aria-label="Kaydı sil"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobil kartlar */}
              <div className="md:hidden space-y-2" data-testid="ciro-mobile-list">
                {kayitlar.length === 0 ? (
                  <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
                    Filtrelere uyan kayıt bulunamadı.
                  </div>
                ) : (
                  kayitlar.map((k) => (
                    <div
                      key={k.id}
                      className="flex items-start gap-3 rounded-xl border bg-card p-3.5"
                      onClick={() => setEditTarget(k)}
                      role="button"
                      data-testid="ciro-mobile-card"
                    >
                      <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                        <Checkbox
                          checked={selected.has(k.id)}
                          onCheckedChange={() => toggleSelect(k.id)}
                          className="h-5 w-5"
                          aria-label="Seç"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground tabular-nums">{formatDate(k.tarih)}</span>
                          <span className="text-xs text-muted-foreground">• {k.belge_turu}</span>
                        </div>
                        <p className="mt-0.5 truncate text-sm font-medium">
                          {k.musteri_aciklama || "—"}
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          <TahsilatBadge durum={k.tahsilat_durumu} vade={k.vade_tarihi} />
                          <span className="text-xs text-muted-foreground">
                            {k.bolum || "Belirtilmemiş"}
                            {k.bolum_dagilimi?.length > 1 && <span className="font-semibold text-sky-700"> +{k.bolum_dagilimi.length - 1}</span>}
                          </span>
                        </div>
                        {(k.hasar_dosya_no || k.plaka_dosya_no || k.is_emri_no) && (
                          <p className="mt-1 text-[11px] text-muted-foreground tabular-nums" data-testid="ciro-mobile-dosya-isemri">
                            {(k.hasar_dosya_no || k.plaka_dosya_no) && <>Dosya: {k.hasar_dosya_no || k.plaka_dosya_no}</>}
                            {(k.hasar_dosya_no || k.plaka_dosya_no) && k.is_emri_no && " • "}
                            {k.is_emri_no && <>İş Emri: {k.is_emri_no}</>}
                          </p>
                        )}
                      </div>
                      <p className="font-display text-sm font-bold tabular-nums whitespace-nowrap">
                        {formatTL(k.toplam, 2)}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* Toplam satırı */}
              {data && (
                <div
                  className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border bg-card px-4 py-3 text-sm"
                  data-testid="ciro-totals-bar"
                >
                  <span className="text-muted-foreground">
                    <strong className="text-foreground tabular-nums">{data.toplamlar.adet.toLocaleString("tr-TR")}</strong> kayıt
                  </span>
                  <span className="text-muted-foreground">
                    Matrah: <strong className="text-foreground tabular-nums" data-testid="ciro-total-matrah">{formatTL(data.toplamlar.matrah, 2)}</strong>
                  </span>
                  <span className="text-muted-foreground">
                    KDV: <strong className="text-foreground tabular-nums">{formatTL(data.toplamlar.kdv, 2)}</strong>
                  </span>
                  <span className="text-muted-foreground">
                    Toplam: <strong className="text-foreground tabular-nums" data-testid="ciro-total-toplam">{formatTL(data.toplamlar.toplam, 2)}</strong>
                  </span>
                </div>
              )}

              {/* Sayfalama */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between" data-testid="ciro-pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => p - 1)}
                    className="min-h-[44px] gap-1"
                    data-testid="ciro-pagination-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Önceki
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    Sayfa {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                    className="min-h-[44px] gap-1"
                    data-testid="ciro-pagination-next"
                  >
                    Sonraki
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ---- TAKİP EDİLECEK DOSYALAR ---- */}
        <TabsContent value="takip" className="mt-4 space-y-4">
          {takipLoading && !takip ? (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !takip ? null : (
            <>
              {/* Dönem seçici */}
              <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card px-4 py-3" data-testid="takip-donem-bar">
                <div>
                  <p className="text-sm font-semibold">Takip Edilecek Dosyalar — {donemEtiketi(takipDonem.yil, takipDonem.ay)}</p>
                  <p className="text-xs text-muted-foreground">Fatura tarihine göre açık (Bekliyor / Kısmi) dosyalar</p>
                </div>
                <div className="ml-auto">
                  <AyYilSecici yil={takipDonem.yil} ay={takipDonem.ay} onChange={setTakipDonem} testid="takip-donem" />
                </div>
              </div>

              {/* Özet */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="takip-ozet">
                {[
                  { label: "Takipteki Dosya", deger: takip.ozet.adet.toLocaleString("tr-TR"), testid: "takip-ozet-adet" },
                  { label: "Toplam Açık Bakiye", deger: formatTL(takip.ozet.toplam_acik), testid: "takip-ozet-acik" },
                  { label: "Gecikmiş Dosya", deger: takip.ozet.gecikmis_adet.toLocaleString("tr-TR"), kirmizi: takip.ozet.gecikmis_adet > 0, testid: "takip-ozet-gecikmis-adet" },
                  { label: "Gecikmiş Tutar", deger: formatTL(takip.ozet.gecikmis_tutar), kirmizi: takip.ozet.gecikmis_tutar > 0, testid: "takip-ozet-gecikmis-tutar" },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl border bg-card p-4" data-testid={s.testid}>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</p>
                    <p className={`mt-1 font-display text-lg font-bold tabular-nums ${s.kirmizi ? "text-red-700" : ""}`}>{s.deger}</p>
                  </div>
                ))}
              </div>

              {/* Takip tablosu */}
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="w-full overflow-x-auto">
                  <table className="w-full text-sm" data-testid="takip-table">
                    <thead className="bg-muted/50">
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-3">Fatura No</th>
                        <th className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() =>
                              setTakipSirala((s) => (s === "vade" ? "is_emri_asc" : s === "is_emri_asc" ? "is_emri_desc" : "vade"))
                            }
                            className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground ${takipSirala !== "vade" ? "text-[hsl(var(--primary))] font-semibold" : ""}`}
                            title="İş Emri No'ya göre sırala"
                            data-testid="takip-is-emri-sirala-button"
                          >
                            İş Emri No
                            {takipSirala === "is_emri_asc" ? <ArrowUp className="h-3 w-3" /> : takipSirala === "is_emri_desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3" />}
                          </button>
                        </th>
                        <th className="px-3 py-3">Plaka / Dosya No</th>
                        <th className="px-3 py-3">Sigorta / Müşteri</th>
                        <th className="px-3 py-3 text-right">Tutar</th>
                        <th className="px-3 py-3 text-right">Tahsil Edilen</th>
                        <th className="px-3 py-3 text-right">Kalan</th>
                        <th className="px-3 py-3">Vade Tarihi</th>
                        <th className="px-3 py-3">Vade Durumu</th>
                        <th className="px-3 py-3">Statü</th>
                        <th className="px-3 py-3 text-right">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {takip.kayitlar.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="px-3 py-10 text-center text-muted-foreground" data-testid="takip-empty-message">
                            {donemEtiketi(takipDonem.yil, takipDonem.ay)} döneminde takip edilecek açık dosya yok.
                          </td>
                        </tr>
                      ) : (
                        takipKayitlar.map((k) => (
                          <tr
                            key={k.id}
                            className={`border-t border-border/60 transition-colors ${k.gecikme_gun != null ? "bg-red-50/50 hover:bg-red-50" : "hover:bg-[hsl(var(--primary))]/[0.03]"}`}
                            data-testid="takip-table-row"
                          >
                            <td className="px-3 py-2.5 whitespace-nowrap font-medium">
                              {k.belge_no || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-sky-700" data-testid="takip-is-emri-no">
                              {k.is_emri_no || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap" data-testid="takip-plaka-dosya">
                              {k.plaka ? <p className="font-semibold uppercase tracking-wide">{k.plaka}</p> : null}
                              {k.dosya_no ? <p className="text-[11px] tabular-nums text-muted-foreground">{k.dosya_no}</p> : null}
                              {!k.plaka && !k.dosya_no && <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 max-w-[220px]">
                              <p className="truncate" title={k.sigorta_sirketi || k.musteri_aciklama}>
                                {k.sigorta_sirketi || k.musteri_aciklama || <span className="text-muted-foreground">—</span>}
                              </p>
                              {k.sigorta_sirketi && k.musteri_aciklama && k.sigorta_sirketi !== k.musteri_aciklama && (
                                <p className="truncate text-[11px] text-muted-foreground">{k.musteri_aciklama}</p>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.toplam, 2)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-emerald-700">{formatTL(k.tahsil_edilen, 2)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold">
                              {formatTL(k.kalan, 2)}
                              {k.kdv_haric_tahsilat && (
                                <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-800 align-middle" title="KDV'siz tahsilat — hedef matrah tutarıdır" data-testid="takip-kdvsiz-badge">KDV'siz</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">
                              {k.vade_tarihi ? formatDate(k.vade_tarihi) : <span className="text-muted-foreground italic text-xs">Vade girilmemiş</span>}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              {k.gecikme_gun != null ? (
                                <Badge className="bg-red-50 text-red-700 border border-red-200 hover:bg-red-50 gap-1" data-testid="takip-gecikme-badge">
                                  <AlertTriangle className="h-3 w-3" /> {k.gecikme_gun} gün gecikti
                                </Badge>
                              ) : k.vadeye_kalan_gun != null ? (
                                <Badge className={`border hover:bg-transparent ${k.vadeye_kalan_gun <= 7 ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid="takip-kalan-gun-badge">
                                  {k.vadeye_kalan_gun === 0 ? "Bugün vadesi doluyor" : `${k.vadeye_kalan_gun} gün kaldı`}
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5"><TahsilatBadge durum={k.tahsilat_durumu} vade={k.vade_tarihi} /></td>
                            <td className="px-3 py-2.5 text-right whitespace-nowrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setTahsilatCiroId(k.id)}
                                className="min-h-[36px] gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                                data-testid="takip-tahsilat-button"
                              >
                                <Banknote className="h-3.5 w-3.5" /> Tahsilat
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Takip Raporu (durum kırılımı + eksik ödemeler) */}
              <TakipRaporu yil={takipDonem.yil} ay={takipDonem.ay} yenileTetik={takipTetik} onTahsilat={setTahsilatCiroId} />
            </>
          )}
        </TabsContent>

        {/* ---- TAHSİLAT ÖZETİ ---- */}
        <TabsContent value="ozet" className="mt-4 space-y-4">
          {ozetLoading && !ozet ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : !ozet ? null : (
            <>
              {/* Dönem seçici */}
              <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card px-4 py-3" data-testid="tahsilat-ozet-donem-bar">
                <div>
                  <p className="text-sm font-semibold">Tahsilat Özeti — {donemEtiketi(ozetDonem.yil, ozetDonem.ay)}</p>
                  <p className="text-xs text-muted-foreground">Kartlar fatura tarihine, hareketler ve kanal dağılımı tahsilat tarihine göre filtrelenir</p>
                </div>
                <div className="ml-auto">
                  <AyYilSecici yil={ozetDonem.yil} ay={ozetDonem.ay} onChange={setOzetDonem} testid="tahsilat-ozet-donem" />
                </div>
              </div>

              {/* 4 sade kart */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tahsilat-ozet-kpi">
                {[
                  { label: "Toplam Fatura Tutarı", deger: formatTL(ozet.ozet.toplam), alt: ozetDonem.ay ? "Bu ay kesilen faturalar (KDV dahil)" : "KDV dahil tüm kayıtlar", testid: "tahsilat-ozet-toplam" },
                  { label: "Tahsil Edilen", deger: formatTL(ozet.ozet.tahsil_edilen), renk: "text-emerald-700", alt: ozetDonem.ay ? "Bu ay kesilen faturaların tahsilatı" : "Tamamlanan tahsilatlar", testid: "tahsilat-ozet-tahsil" },
                  { label: "Bekleyen", deger: formatTL(ozet.ozet.bekleyen), renk: ozet.ozet.bekleyen > 0 ? "text-amber-700" : "", alt: "Ağustos öncesi devir bakiyeleri hariç", testid: "tahsilat-ozet-bekleyen" },
                  { label: "Vadesi Geçen", deger: formatTL(ozet.ozet.vadesi_gecen), renk: ozet.ozet.vadesi_gecen > 0 ? "text-red-700" : "text-emerald-700", alt: "Vadesi geçmiş bekleyenler", testid: "tahsilat-ozet-vadesi-gecen" },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl border bg-card p-4" data-testid={s.testid}>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</p>
                    <p className={`mt-1 font-display text-xl font-bold tabular-nums ${s.renk || ""}`}>{s.deger}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{s.alt}</p>
                  </div>
                ))}
              </div>

              {/* 12 aylık karşılaştırma */}
              <TahsilatAylikTablo
                aylik={ozet.aylik}
                yil={ozet.aylik_yil}
                seciliAy={ozetDonem.ay}
                onAySec={(m) => setOzetDonem((d) => ({ yil: ozet.aylik_yil, ay: d.ay === m ? null : m }))}
              />

              {/* Ödeme kanalı dağılımı (toplam) */}
              <div className="rounded-xl border bg-card overflow-hidden" data-testid="odeme-kanal-dagilimi">
                <div className="border-b bg-muted/40 px-4 py-3">
                  <p className="text-sm font-semibold">Ödeme Kanalı Dağılımı ({ozetDonem.ay ? donemEtiketi(ozetDonem.yil, ozetDonem.ay) : "Toplam"})</p>
                  <p className="text-xs text-muted-foreground">Tahsilatlar kanal bazında — Yönetim Tahsilatı satırının altında kişi kırılımı</p>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-2.5">Ödeme Kanalı</th>
                      <th className="px-4 py-2.5 text-right">İşlem</th>
                      <th className="px-4 py-2.5 text-right">Toplam</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ozet.odeme_kanallari || []).length === 0 ? (
                      <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground" data-testid="odeme-kanal-bos">Henüz tahsilat yok.</td></tr>
                    ) : (
                      (ozet.odeme_kanallari || []).map((k) => (
                        <Fragment key={k.odeme_yeri}>
                          <tr className="border-t border-border/60" data-testid={`odeme-kanal-${k.odeme_yeri}`}>
                            <td className="px-4 py-2.5 font-medium">
                              <span className={
                                k.odeme_yeri === "Yönetim Tahsilatı" ? "text-red-700"
                                : k.odeme_yeri === "Banka" ? "text-sky-700"
                                : k.odeme_yeri === "Kasa" ? "text-emerald-700"
                                : k.odeme_yeri === "Tedarikçi Ödemesi" ? "text-violet-700"
                                : ""
                              }>{k.odeme_yeri}</span>
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{k.adet.toLocaleString("tr-TR")}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap">{formatTL(k.toplam, 2)}</td>
                          </tr>
                          {k.odeme_yeri === "Yönetim Tahsilatı" &&
                            (ozet.yonetim_tahsilati || []).map((y) => (
                              <tr key={`yt-${y.alan_kisi}`} className="border-t border-border/30 bg-red-50/40" data-testid="odeme-kanal-yonetim-kisi">
                                <td className="px-4 py-2 pl-10 text-xs text-red-800">↳ {y.alan_kisi || "Belirtilmemiş"}</td>
                                <td className="px-4 py-2 text-right tabular-nums text-xs text-muted-foreground">{y.adet.toLocaleString("tr-TR")}</td>
                                <td className="px-4 py-2 text-right tabular-nums text-xs font-semibold text-red-800 whitespace-nowrap">{formatTL(y.toplam, 2)}</td>
                              </tr>
                            ))}
                        </Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Tahsilat hareketleri */}
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="border-b bg-muted/40 px-4 py-3">
                  <p className="text-sm font-semibold">Tahsilat Hareketleri {ozetDonem.ay ? `— ${donemEtiketi(ozetDonem.yil, ozetDonem.ay)}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{ozetDonem.ay ? `Bu ay yapılan tahsilatlar (${ozet.hareketler.length} işlem)` : "Son tahsilatlar"} — satır sonundaki butonlarla düzenleyebilir veya silebilirsiniz</p>
                </div>
                <div className="w-full overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tahsilat-hareketler-table">
                    <thead className="bg-muted/50">
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2.5">Tarih</th>
                        <th className="px-3 py-2.5">Fatura No</th>
                        <th className="px-3 py-2.5">İş Emri No</th>
                        <th className="px-3 py-2.5">Dosya No</th>
                        <th className="px-3 py-2.5">Sigorta</th>
                        <th className="px-3 py-2.5">Müşteri</th>
                        <th className="px-3 py-2.5 text-right">Tutar</th>
                        <th className="px-3 py-2.5">Alan Kişi</th>
                        <th className="px-3 py-2.5">Nereye Ödendi</th>
                        <th className="px-3 py-2.5 text-right">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ozet.hareketler.length === 0 ? (
                        <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground" data-testid="tahsilat-hareket-bos">{ozetDonem.ay ? "Bu ay tahsilat hareketi yok." : "Henüz tahsilat hareketi yok."}</td></tr>
                      ) : (
                        ozet.hareketler.map((h) => (
                          <tr key={h.id} className="border-t border-border/60 hover:bg-[hsl(var(--primary))]/[0.03]" data-testid="tahsilat-hareket-row">
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{formatDate(h.tarih)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums" data-testid="tahsilat-hareket-fatura-no">
                              {h.belge_no || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-sky-700" data-testid="tahsilat-hareket-is-emri-no">
                              {h.is_emri_no || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums" data-testid="tahsilat-hareket-dosya-no">
                              {h.dosya_no || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 max-w-[160px] truncate" title={h.sigorta_sirketi} data-testid="tahsilat-hareket-sigorta">
                              {h.sigorta_sirketi || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 max-w-[160px] truncate" title={h.musteri} data-testid="tahsilat-hareket-musteri">
                              {h.musteri || <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold">{formatTL(h.tutar, 2)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">{h.alan_kisi}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <Badge className={`border text-[10px] font-semibold hover:bg-transparent ${
                                h.odeme_yeri === "Yönetim Tahsilatı" ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-50"
                                : h.odeme_yeri === "Tedarikçi Ödemesi" ? "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-50"
                                : h.odeme_yeri === "Banka" ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50"
                                : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50"
                              }`}>
                                {h.odeme_yeri}{h.tedarikci ? ` — ${h.tedarikci}` : ""}
                              </Badge>
                            </td>
                            <td className="px-3 py-2.5 text-right whitespace-nowrap">
                              <div className="inline-flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setTahsilatCiroId(h.ciro_id)}
                                  disabled={!h.ciro_id}
                                  title="Tahsilatı düzenle"
                                  data-testid="tahsilat-hareket-duzenle-button"
                                >
                                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setSilTahsilat(h)}
                                  title="Tahsilatı sil"
                                  data-testid="tahsilat-hareket-sil-button"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Detay raporları — sade görünüm için varsayılan kapalı */}
              <Accordion type="single" collapsible className="rounded-xl border bg-card px-4" data-testid="tahsilat-ozet-detay-accordion">
                <AccordionItem value="detay" className="border-0">
                  <AccordionTrigger className="py-3.5 text-sm font-semibold hover:no-underline" data-testid="tahsilat-ozet-detay-trigger">
                    Detay Raporları — Sigorta / Tedarikçi / Yönetim
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-4">
              <div className="grid gap-4 lg:grid-cols-2">
                {/* Sigorta bazında bekleyenler (kompakt) */}
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="border-b bg-muted/40 px-4 py-3">
                    <p className="text-sm font-semibold">Sigorta Bazında Bekleyenler</p>
                    <p className="text-xs text-muted-foreground">Hangi sigorta şirketinden ne bekleniyor</p>
                  </div>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tahsilat-ozet-sigorta-table">
                      <thead className="bg-muted/50">
                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2.5">Sigorta Şirketi</th>
                          <th className="px-3 py-2.5 text-right">Dosya</th>
                          <th className="px-3 py-2.5 text-right">Bekleyen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ozet.sigorta_bekleyen.length === 0 ? (
                          <tr><td colSpan={3} className="px-3 py-8 text-center text-muted-foreground">Bekleyen sigorta alacağı yok.</td></tr>
                        ) : (
                          ozet.sigorta_bekleyen.slice(0, 10).map((s) => (
                            <tr key={s.ad} className="border-t border-border/60" data-testid="tahsilat-ozet-sigorta-row">
                              <td className="px-3 py-2.5 max-w-[200px] truncate font-medium" title={s.ad}>{s.ad}</td>
                              <td className="px-3 py-2.5 text-right tabular-nums">{s.adet.toLocaleString("tr-TR")}</td>
                              <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold text-amber-700">{formatTL(s.bekleyen)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Tedarikçi ödemeleri */}
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="border-b bg-muted/40 px-4 py-3 flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold">Tedarikçi Ödemeleri</p>
                      <p className="text-xs text-muted-foreground">Hangi tedarikçiye ne kadar ödendi</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 gap-1.5"
                      onClick={tedarikciExcelIndir}
                      disabled={ozet.tedarikci_odemeleri.length === 0}
                      data-testid="tedarikci-excel-indir-btn"
                    >
                      <Download className="h-4 w-4" /> Excel'e Aktar
                    </Button>
                  </div>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tedarikci-odemeleri-table">
                      <thead className="bg-muted/50">
                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2.5">Tedarikçi</th>
                          <th className="px-3 py-2.5 text-right">Ödeme</th>
                          <th className="px-3 py-2.5 text-right">Toplam</th>
                          <th className="px-3 py-2.5">Son Ödeme</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ozet.tedarikci_odemeleri.length === 0 ? (
                          <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground" data-testid="tedarikci-odeme-bos">Tedarikçi ödemesi yok.</td></tr>
                        ) : (
                          ozet.tedarikci_odemeleri.map((t) => (
                            <tr key={t.tedarikci} className="border-t border-border/60" data-testid="tedarikci-odeme-row">
                              <td className="px-3 py-2.5 max-w-[200px] truncate font-medium" title={t.tedarikci}>{t.tedarikci}</td>
                              <td className="px-3 py-2.5 text-right tabular-nums">{t.adet.toLocaleString("tr-TR")}</td>
                              <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold text-violet-700">{formatTL(t.toplam, 2)}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{t.son_tarih ? formatDate(t.son_tarih) : "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Yönetim Tahsilatı (kişi bazında) — sadece kayıt varsa gösterilir */}
              {ozet.yonetim_tahsilati.length > 0 && (
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="border-b bg-muted/40 px-4 py-3 flex items-center gap-2">
                    <User className="h-4 w-4 text-red-700" />
                    <div>
                      <p className="text-sm font-semibold">Yönetim Tahsilatı (Kişi Bazında)</p>
                      <p className="text-xs text-muted-foreground">Tahsilatı kendine alan yöneticilerin toplamları</p>
                    </div>
                  </div>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tahsilat-ozet-yonetim-table">
                      <thead className="bg-muted/50">
                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2.5">Kişi</th>
                          <th className="px-3 py-2.5 text-right">Tahsilat Adedi</th>
                          <th className="px-3 py-2.5 text-right">Toplam</th>
                          <th className="px-3 py-2.5">Son Tahsilat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ozet.yonetim_tahsilati.map((k) => (
                          <tr key={k.alan_kisi} className="border-t border-border/60 hover:bg-red-50/40" data-testid="tahsilat-ozet-yonetim-row">
                            <td className="px-3 py-2.5 font-medium">{k.alan_kisi}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{k.adet.toLocaleString("tr-TR")}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold text-red-700">{formatTL(k.toplam, 2)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{k.son_tarih ? formatDate(k.son_tarih) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* ---- TAHSİLAT RAPORU (tarih aralıklı: kim aldı, nereye ödendi) ---- */}
              <TahsilatRaporu />
            </>
          )}
        </TabsContent>

        {/* ---- USTA PERFORMANSI ---- */}
        <TabsContent value="usta" className="mt-4">
          <UstaRaporu yil={2026} />
        </TabsContent>
      </Tabs>

      {/* Tahsilat dialogu */}
      <TahsilatDialog
        ciroId={tahsilatCiroId}
        open={!!tahsilatCiroId}
        onClose={() => setTahsilatCiroId(null)}
        onChanged={() => {
          fetchList();
          fetchTakip();
          if (tab === "ozet") fetchOzet();
        }}
      />

      {/* Toplu tahsilat dialogu */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-card sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Toplu Tahsilat</DialogTitle>
            <DialogDescription>
              {selected.size} kayıt “Tahsil Edildi” olarak işaretlenecek.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Tahsilat Tarihi</Label>
            <Input
              type="date"
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
              className="h-12"
              data-testid="ciro-bulk-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} className="min-h-[44px]">
              Vazgeç
            </Button>
            <Button
              onClick={handleBulkTahsilat}
              disabled={bulkSaving}
              className="min-h-[44px]"
              data-testid="ciro-bulk-confirm-button"
            >
              {bulkSaving ? "Güncelleniyor…" : "Onayla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Düzenleme dialogu */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto w-[96vw] sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display flex flex-wrap items-center justify-between gap-2">
              <span>Ciro Kaydını Düzenle</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mr-6 h-9 gap-1.5 text-xs font-semibold"
                onClick={() => {
                  const id = editTarget?.id;
                  setEditTarget(null);
                  setTahsilatCiroId(id);
                }}
                data-testid="ciro-duzenle-tahsilatlar-button"
              >
                <Banknote className="h-4 w-4" /> Tahsilatlar (Düzenle / Sil)
              </Button>
            </DialogTitle>
            <DialogDescription>
              {editTarget && `${formatDate(editTarget.tarih)} • ${formatTL(editTarget.toplam, 2)}`}
              {editTarget?.kaynak === "excel" && " • Excel'den aktarıldı"}
            </DialogDescription>
          </DialogHeader>
          {editTarget && (
            <CiroForm
              key={editTarget.id}
              initial={editTarget}
              onSubmit={handleUpdate}
              submitting={saving}
              submitLabel="Güncelle"
            />
          )}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(editTarget)}
              className="min-h-[44px] gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
              data-testid="ciro-edit-delete-button"
            >
              <Trash2 className="h-4 w-4" />
              Kaydı Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Silme onayı */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Kaydı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu kaydı silmek istediğine emin misin? Silinen kayıtlar denetim için loglanır.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="min-h-[44px] bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="ciro-delete-confirm-button"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mükerrer dosya no uyarısı */}
      <AlertDialog open={!!mukerrer} onOpenChange={(o) => !o && setMukerrer(null)}>
        <AlertDialogContent className="bg-card" data-testid="mukerrer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Bu Dosya No'ya Ait Kayıt Zaten Var</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Bu dosya no'ya ait kayıt zaten var
                  {mukerrer?.mevcut?.is_emri_no ? ` (İş Emri No: ${mukerrer.mevcut.is_emri_no})` : ""}. Fatura bu kayda eklensin mi?
                </p>
                {mukerrer?.mevcut && (
                  <div className="rounded-lg border bg-muted/40 p-3 text-xs space-y-1" data-testid="mukerrer-mevcut-detay">
                    <p><span className="text-muted-foreground">Mevcut kayıt:</span> <strong>{mukerrer.mevcut.kayit_tipi || "Fatura"}</strong>{mukerrer.mevcut.belge_no ? ` — ${mukerrer.mevcut.belge_no}` : ""}</p>
                    <p><span className="text-muted-foreground">Dosya No:</span> {mukerrer.mevcut.plaka_dosya_no || mukerrer.mevcut.hasar_dosya_no || "—"}</p>
                    <p><span className="text-muted-foreground">Müşteri:</span> {mukerrer.mevcut.sigorta_sirketi || mukerrer.mevcut.musteri_aciklama || "—"}</p>
                    <p><span className="text-muted-foreground">Tutar:</span> {formatTL(mukerrer.mevcut.toplam || 0, 2)} • {mukerrer.mevcut.tarih ? formatDate(mukerrer.mevcut.tarih) : "—"}</p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Onaylarsanız fatura no ve tutar mevcut kayda işlenir, dosya "Takip Edilecek Dosyalar" listesine düşer. Yeni kayıt oluşturulmaz.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="min-h-[44px]"
              onClick={() => {
                const m = mukerrer;
                setMukerrer(null);
                prefillDoldur(m.alanlar, m.pdf_id, m.dosya_adi, m.hata);
              }}
              data-testid="mukerrer-yeni-kayit-button"
            >
              Hayır, Yeni Kayıt Olarak Devam Et
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleMukerrerEkle();
              }}
              disabled={mukerrerIsleniyor}
              className="min-h-[44px]"
              data-testid="mukerrer-kayda-ekle-button"
            >
              {mukerrerIsleniyor ? "İşleniyor…" : mukerrerFaturaMi ? "Evet, Bu Kayda Ekle" : "Evet, Eksikleri Tamamla"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tahsilat silme onayı */}
      <AlertDialog open={!!silTahsilat} onOpenChange={(o) => !o && setSilTahsilat(null)}>
        <AlertDialogContent className="bg-card" data-testid="tahsilat-sil-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Tahsilatı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              {silTahsilat && `${formatDate(silTahsilat.tarih)} tarihli ${formatTL(silTahsilat.tutar, 2)} tutarındaki tahsilat silinecek. Bağlı kasa hareketi de kaldırılır ve işlem loglanır.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleTahsilatSil();
              }}
              disabled={silTahsilatIsleniyor}
              className="min-h-[44px] bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="tahsilat-sil-onay-button"
            >
              {silTahsilatIsleniyor ? "Siliniyor…" : "Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
